/**
 * FacturesPage — composant partagé pour toutes les pages de facturation.
 * Utilisé par : Location, Distribution, Réparation (futur).
 *
 * Props :
 *   title         {string}    Titre de la page
 *   fetchFn       {fn}        Fonction API → retourne { data: [...] } ou [...]
 *   getPdfFn      {fn}        (facture_id) → arraybuffer
 *   getDocumentOrigine {fn}   (facture) → { label, href? }
 *   extraFiltres  {node}      Filtres supplémentaires (optionnel)
 *
 * Aperçu PDF :
 *   1. Tente de récupérer depuis Supabase (pdf_path)
 *   2. Si absent → auto-sync Odoo une fois (ir.attachment.search_read)
 *   // TODO Pennylane : remplacer/compléter l'auto-sync par l'API Pennylane
 *   //   quand l'intégration sera activée (vérifier org_integrations.provider='pennylane')
 *   3. Si toujours absent → toast "Facture probablement en brouillon"
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, RefreshCw, Loader2, Receipt,
  ChevronLeft, ChevronRight, X, Eye, Download, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSociete } from '../context/SocieteContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

const MOIS_FR = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

function fmtEur(v) {
  return (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function fmtDate(s) {
  if (!s) return '—';
  return s.split('T')[0].split('-').reverse().join('/');
}

// ── StatutBadge ────────────────────────────────────────────────────────────────

const STATUT_CONFIG = {
  brouillon:           { label: 'À valider',            bg: '#FFFBEB', color: '#B45309', border: '#FCD34D' },
  valide:              { label: 'Facturée',             bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  annule:              { label: 'Annulé',               bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
  non_transmise:       { label: 'Non transmise',        bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
  en_attente:          { label: 'À valider',            bg: '#FFFBEB', color: '#B45309', border: '#FCD34D' },
  partiellement_payee: { label: 'Partiellement payée',  bg: '#FFF7ED', color: '#B45309', border: '#FDE68A' },
  payee:               { label: 'Payée',                bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  en_paiement:         { label: 'En paiement',          bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  annulee:             { label: 'Annulée',              bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
};

function StatutBadge({ statut }) {
  const cfg = STATUT_CONFIG[statut] || { label: statut || '—', bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, borderRadius: 100, padding: '2px 8px',
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

// ── Modale aperçu PDF ──────────────────────────────────────────────────────────

function PdfPreviewModal({ data, onClose, onDownload }) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col"
      style={{ background: 'rgba(15,23,42,0.82)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ background: '#1E293B', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)' }}>
            <Eye size={14} color="white" />
          </div>
          <span className="text-sm font-semibold text-white">{data.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onDownload}
            data-testid="preview-modal-download"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ background: '#16A34A', color: 'white' }}>
            <Download size={12} /> Télécharger
          </button>
          <button onClick={onClose}
            data-testid="preview-modal-close"
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.7)' }}>
            <X size={15} />
          </button>
        </div>
      </div>
      <iframe src={data.url} title={`Aperçu ${data.name}`}
        className="flex-1 w-full border-0" style={{ minHeight: 0 }} />
    </div>
  );
}

// ── Composant principal ────────────────────────────────────────────────────────

export default function FacturesPage({ title, fetchFn, getPdfFn, syncFn, onRefreshFn, getDocumentOrigine, extraFiltres, deleteFn, clientLabel = 'Client' }) {
  const { activeAgences } = useSociete();
  const now = new Date();

  // État
  const [factures, setFactures]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [mois, setMois]               = useState(now.getMonth()); // 0-11
  const [annee, setAnnee]             = useState(now.getFullYear());
  const [previewing, setPreviewing]         = useState(null);
  const [previewData, setPreviewData]       = useState(null);
  const [deleting, setDeleting]             = useState(null);
  const timerRef = useRef(null);

  // Debounce recherche
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timerRef.current);
  }, [searchInput]);

  // Chargement — envoie annee+mois au backend pour pre-filtrer correctement
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        annee,
        mois: mois + 1,           // frontend 0-11 → backend 1-12
        search: search || undefined,
      };
      if (activeAgences?.length) {
        params.agence_ids = activeAgences.map(a => a.agence_id).join(',');
      }
      const res = await fetchFn(params);
      setFactures(res.data?.data || res.data || []);
    } catch {
      toast.error('Erreur de chargement des factures');
    } finally {
      setLoading(false);
    }
  }, [fetchFn, search, activeAgences, annee, mois]);

  useEffect(() => { load(); }, [load]);

  // Filtre local — cohérent avec le filtre backend annee/mois
  const facturiesFiltrees = factures.filter(f => {
    if (filtreStatut && f.statut !== filtreStatut) return false;
    // Si pas de date, on inclut la facture plutôt que de la masquer
    const raw = f.date_facture || f.created_at;
    if (!raw) return true;
    // Normaliser : accepte "2026-05-14", "2026-05-14T..." ou "2026-05-14 ..."
    const d = String(raw).replace('T', ' ').split(' ')[0];
    const parts = d.split('-');
    if (parts.length < 2) return true;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    if (!y || !m) return true;
    return y === annee && m === mois + 1;
  });

  // Navigation mois
  const prevMois = () => {
    if (mois === 0) { setMois(11); setAnnee(a => a - 1); }
    else setMois(m => m - 1);
  };
  const nextMois = () => {
    if (mois === 11) { setMois(0); setAnnee(a => a + 1); }
    else setMois(m => m + 1);
  };

  // PDF helpers
  const closePreview = () => {
    if (previewData?.url) URL.revokeObjectURL(previewData.url);
    setPreviewData(null);
  };

  const handleApercu = async (facture) => {
    setPreviewing(facture.facture_id);
    try {
      // Sync silencieuse des métadonnées Odoo (nom, TTC, date échéance) si syncFn fourni
      if (syncFn) {
        try { await syncFn(facture.facture_id); } catch (_e) { /* non bloquant */ }
      }
      const res = await getPdfFn(facture.facture_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      setPreviewData({
        url,
        name: facture.odoo_invoice_name || facture.facture_id,
      });
      // Rafraîchir silencieusement pour afficher les données à jour
      load();
    } catch (e) {
      let msg = 'PDF non disponible — la facture est probablement encore en brouillon. Pensez à la confirmer dans Odoo.';
      if (e.response?.data instanceof ArrayBuffer) {
        try { msg = JSON.parse(new TextDecoder().decode(e.response.data))?.detail || msg; } catch {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setPreviewing(null);
    }
  };

  const handleDownload = () => {
    if (!previewData) return;
    const a = document.createElement('a');
    a.href = previewData.url;
    a.download = `${previewData.name.replace('/', '-')}.pdf`;
    a.click();
  };

  const handleDelete = async (facture) => {
    if (!deleteFn) return;
    const label = facture.odoo_invoice_name || facture.facture_id;
    if (!window.confirm(`Supprimer la facture ${label} ?\nL'OR sera réinitialisé et pourra être re-facturé.`)) return;
    setDeleting(facture.facture_id);
    try {
      await deleteFn(facture.facture_id);
      toast.success(`Facture ${label} supprimée`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PdfPreviewModal data={previewData} onClose={closePreview} onDownload={handleDownload} />

      {/* ── Barre filtres ── */}
      <div className="flex-shrink-0 px-6 py-3 border-b flex items-center gap-3 flex-wrap"
        style={{ borderColor: 'var(--border)', background: 'white' }}>

        {/* Recherche */}
        <div className="relative flex-1 min-w-48 max-w-72">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-disabled)' }} />
          <input
            data-testid="factures-search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="N° facture, client…"
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border outline-none"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); setSearch(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100">
              <X size={11} style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>

        {/* Filtre statut */}
        <select
          data-testid="factures-filter-statut"
          value={filtreStatut}
          onChange={e => setFiltreStatut(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border outline-none"
          style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}>
          <option value="">Tous les statuts</option>
          <option value="brouillon">Brouillon</option>
          <option value="valide">Validé</option>
          <option value="annule">Annulé</option>
        </select>

        {/* Navigateur mois/année */}
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg border"
          style={{ borderColor: 'var(--border)' }}>
          <button onClick={prevMois} data-testid="month-prev"
            className="p-1 rounded hover:bg-gray-100">
            <ChevronLeft size={13} />
          </button>
          <span className="text-sm font-bold w-32 text-center select-none"
            style={{ color: 'var(--text)' }}>
            {MOIS_FR[mois]} {annee}
          </span>
          <button onClick={nextMois} data-testid="month-next"
            className="p-1 rounded hover:bg-gray-100">
            <ChevronRight size={13} />
          </button>
        </div>

        {extraFiltres}

        {/* Compteur */}
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {loading ? '…' : `${facturiesFiltrees.length} facture${facturiesFiltrees.length !== 1 ? 's' : ''}`}
        </span>

        {/* Refresh */}
        <button onClick={async () => {
          if (onRefreshFn) {
            setLoading(true);
            try { await onRefreshFn(); } catch (_) {}
          }
          load();
        }} disabled={loading}
          data-testid="factures-refresh"
          className="p-1.5 rounded-lg border hover:bg-gray-50 ml-auto"
          style={{ borderColor: 'var(--border)' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* ── Corps ── */}
      {loading && factures.length === 0 ? (
        <div className="flex items-center justify-center flex-1">
          <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {facturiesFiltrees.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-24">
              <Receipt size={36} style={{ color: 'var(--text-disabled)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {factures.length === 0
                  ? 'Aucune facture — elles apparaissent ici après confirmation dans Odoo'
                  : `Aucune facture pour ${MOIS_FR[mois]} ${annee}`}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'var(--bg-subtle, #F9FAFB)', borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold"
                    style={{ color: 'var(--text-muted)', width: '130px' }}>N° Facture</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold"
                    style={{ color: 'var(--text-muted)', width: '220px' }}>{clientLabel}</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold"
                    style={{ color: 'var(--text-muted)', width: '110px' }}>Document d'origine</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>HT</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>TTC</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>Date facture</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>Échéance</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold w-24"
                    style={{ color: 'var(--text-muted)' }}>Statut</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold w-20"
                    style={{ color: 'var(--text-muted)' }}>Aperçu</th>
                  {deleteFn && (
                    <th className="text-center px-3 py-2.5 text-xs font-semibold w-12"
                      style={{ color: 'var(--text-muted)' }}></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {facturiesFiltrees.map((f, i) => {
                  const doc = getDocumentOrigine(f);
                  return (
                    <tr key={f.facture_id}
                      data-testid={`facture-row-${f.facture_id}`}
                      style={{
                        background: i % 2 === 0 ? 'white' : 'var(--bg-subtle, #FAFAFA)',
                        borderBottom: '1px solid var(--border)',
                      }}>

                      {/* N° Facture */}
                      <td className="px-3 py-2.5 text-xs truncate" style={{ width: '130px' }}>
                        {f.odoo_invoice_name ? (
                          <span className="font-semibold" style={{ color: 'var(--primary-solid)' }}>
                            {f.odoo_invoice_name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>

                      {/* Client */}
                      <td className="px-3 py-2.5 truncate font-medium text-xs"
                        style={{ color: 'var(--text)', width: '270px' }}>
                        {f.client_nom || f.entreprise_nom || '—'}
                      </td>

                      {/* Document d'origine */}
                      <td className="px-3 py-2.5 text-xs truncate" style={{ width: '110px' }}>
                        {doc.href ? (
                          <a href={doc.href}
                            className="font-semibold hover:underline"
                            style={{ color: 'var(--primary-solid)' }}>
                            {doc.label}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>{doc.label || '—'}</span>
                        )}
                      </td>

                      {/* HT */}
                      <td className="px-3 py-2.5 text-center text-xs tabular-nums font-semibold"
                        style={{ color: 'var(--text-secondary)' }}>
                        {f.total_ht != null ? fmtEur(f.total_ht) : '—'}
                      </td>

                      {/* TTC */}
                      <td className="px-3 py-2.5 text-center text-xs tabular-nums font-semibold"
                        style={{ color: 'var(--text)' }}>
                        {f.total_ttc != null ? fmtEur(f.total_ttc) : '—'}
                      </td>

                      {/* Date facture */}
                      <td className="px-3 py-2.5 text-center text-xs tabular-nums"
                        style={{ color: 'var(--text-muted)' }}>
                        {fmtDate(f.date_facture)}
                      </td>

                      {/* Échéance */}
                      <td className="px-3 py-2.5 text-center text-xs tabular-nums"
                        style={{ color: 'var(--text-muted)' }}>
                        {fmtDate(f.date_echeance)}
                      </td>

                      {/* Statut */}
                      <td className="px-3 py-2.5 text-center">
                        <StatutBadge statut={
                          (['payee', 'partiellement_payee', 'en_paiement', 'annulee'].includes(f.statut_paiement))
                            ? f.statut_paiement
                            : (f.statut || f.statut_paiement)
                        } />
                      </td>

                      {/* Aperçu */}
                      <td className="px-3 py-2.5 text-center">
                        <button
                          data-testid={`btn-apercu-${f.facture_id}`}
                          onClick={() => handleApercu(f)}
                          disabled={previewing === f.facture_id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors hover:bg-blue-50"
                          style={{ borderColor: '#BFDBFE', color: '#1D4ED8', background: '#EFF6FF' }}>
                          {previewing === f.facture_id
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Eye size={11} />}
                          Aperçu
                        </button>
                      </td>

                      {/* Supprimer (optionnel) */}
                      {deleteFn && (
                        <td className="px-3 py-2.5 text-center">
                          <button
                            data-testid={`btn-supprimer-${f.facture_id}`}
                            onClick={() => handleDelete(f)}
                            disabled={deleting === f.facture_id}
                            title="Supprimer cette facture et réinitialiser l'OR"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors hover:bg-red-50"
                            style={{ borderColor: '#FECACA', color: '#B91C1C', background: 'white' }}>
                            {deleting === f.facture_id
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Trash2 size={12} />}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
